import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, computed, inject, input, output } from '@angular/core';
import { ChartOption }                                         from '@cs/components/chart-svg';
import { CsChatMessage }                                       from './chat.message';
import { CsChartSpec, CsChatSegment, splitChatSegments }       from './cs-chat-segments';
import { dataUrlToBlob, svgToPngDataUrl }                      from './cs-chart-export';
import { CS_CHAT_GLOSSARY, CsGlossarySource, CsGlossaryTerm, matchGlossaryTerms } from './cs-glossary';
import { CS_CHAT_ARTIFACT_URL }                                from './chat.config';

/**
 * One transcript row. This is the key performance boundary: it is OnPush and
 * reads its message through a signal input, so it re-renders only when the
 * *reference* of its `message` changes. Combined with the transcript's
 * `@for (… ; track message.id)` and the host replacing only the streaming
 * message object on each delta, a streamed token re-renders exactly this row
 * and leaves every other row untouched — the Angular equivalent of T3 Code's
 * `memo(TimelineRowContent)` over reference-stable rows.
 */
@Component({
	standalone:      false,
	selector:        'cs-chat-message',
	templateUrl:     './cs-chat-message.component.html',
	styleUrls:       ['./cs-chat-message.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host:            {
		class:                                    'cs-chat__message',
		'[class.cs-chat__message--user]':         "message().role === 'user'",
		'[class.cs-chat__message--assistant]':    "message().role === 'assistant'",
		'[class.cs-chat__message--system]':       "message().role === 'system'",
		'[class.cs-chat__message--streaming]':    'message().streaming === true'
	}
})
export class CsChatMessageComponent {
	readonly message = input.required<CsChatMessage>();

	/**
	 * Whether to show engine diagnostics in the stats footer — the provider/model names the turn ran
	 * on. Off by default: model configuration is internal setup detail, so it rides the same
	 * `Admin.Advanced` entitlement as the advanced debug view. The plain response time stays visible
	 * to everyone (useful, non-revealing feedback).
	 */
	readonly showDiagnostics = input(false);

	/** Emitted when the user clicks "Explain simply" — the host turns it into a plain-language re-ask. */
	readonly explain = output<CsChatMessage>();

	private readonly glossary = inject(CS_CHAT_GLOSSARY, { optional: true }) as CsGlossarySource | null;

	/**
	 * Glossary terms that appear in this message's body — the "Terms in this answer" panel. Empty
	 * while streaming (the body is still arriving) and when no glossary is configured.
	 */
	protected readonly glossaryTerms = computed<CsGlossaryTerm[]>(() => {
		const msg = this.message();
		// Assistant rows only. A 'system' row is an error notice, not an answer — glossing a term that
		// happens to appear in "the provider is rate-limiting requests" would be nonsense.
		if (msg.role !== 'assistant' || msg.streaming === true) { return []; }
		const terms = this.glossary?.terms() ?? [];
		if (!terms.length) { return []; }
		const body = this.bodySegments().filter(s => s.kind === 'markdown').map(s => s.text ?? '').join('\n');
		return matchGlossaryTerms(body, terms);
	});

	/** Re-ask the assistant to explain this answer without jargon. */
	protected requestExplain(): void {
		this.explain.emit(this.message());
	}

	private readonly artifactUrlResolver = inject(CS_CHAT_ARTIFACT_URL, { optional: true });

	/**
	 * Download URL for an artifact chip. Uses the host-provided resolver (same base as the chat
	 * stream — honours the `/ai` path base and any remote instance); falls back to a root-relative
	 * path when no resolver is provided.
	 */
	protected artifactHref(id: number | undefined): string {
		if (id == null) { return ''; }
		return this.artifactUrlResolver ? this.artifactUrlResolver(id) : `/api/chat/artifacts/${id}`;
	}

	/** Content split into markdown / chart segments. User rows render verbatim (handled in the
	 *  template), so only assistant AND system rows are segmented here. */
	protected readonly segments = computed<CsChatSegment[]>(() => {
		const msg = this.message();
		return msg.role === 'user'
			? []
			: splitChatSegments(msg.content, msg.streaming === true);
	});

	/** Body segments (stats excluded — it renders in the footer line, beside the actions). */
	protected readonly bodySegments = computed<CsChatSegment[]>(() =>
		this.segments().filter(s => s.kind !== 'stats'));

	/** The trailing response-stats segment, if the message carries one. */
	protected readonly statsSegment = computed<CsChatSegment | undefined>(() =>
		this.segments().find(s => s.kind === 'stats'));

	/**
	 * Full performance breakdown for the stats hover tooltip. The footer shows only the headline
	 * (total time + model); the detail — tokens, tok/s, time-to-first-token, tool time — surfaces here.
	 */
	protected statsTooltip(s: NonNullable<CsChatSegment['stats']>): string {
		// Total time + model are already shown in the headline, so the tooltip carries only the
		// extra detail: tokens, throughput, time-to-first-token, and tool time.
		const parts: string[] = [];
		if (s.tokens > 0) {
			parts.push(`${s.tokens} tokens`);
			parts.push(`${s.tokPerSec} tok/s`);
		}
		parts.push(`first token ${s.ttfSeconds}s`);
		if (s.toolsSeconds > 0) { parts.push(`tools ${s.toolsSeconds}s`); }
		return parts.join(' · ');
	}

	/** Chart sizing/styling shared by every chart in the transcript. */
	protected readonly chartHeight = 300;

	/**
	 * Per-spec height: most charts take the fixed default, but a decomposition tree's
	 * height is dictated by its WIDEST sibling list — every node needs room for its
	 * bar + label + value, or rows overlap.
	 */
	protected chartHeightFor(spec: CsChartSpec): number {
		if (spec.type !== 'decomposition' || !spec.data.tree) { return this.chartHeight; }
		let widest = 1;
		const walk = (node: { children?: any[] }): void => {
			if (node.children?.length) {
				widest = Math.max(widest, node.children.length);
				node.children.forEach(walk);
			}
		};
		walk(spec.data.tree);
		return Math.max(this.chartHeight, 100 + widest * 56);
	}
	// Cohelion styleguide palette: primary blue lead, then the styleguide accents
	// (green, amber, red, purple, deep blue) and grey-hard for the tail.
	protected readonly chartOptions: ChartOption = {
		colors:        ['#3884ff', '#13ac3e', '#ffb73e', '#f53b3b', '#af26d1', '#2d6acc', '#7a8c96'],
		fontFamily:    'inherit',
		titlePosition: 'none',   // we render the title ourselves as a small caption
		showLegend:    true,
		animateIntro:  false
	};

	/** Index of the chart whose "copied" confirmation is currently showing. */
	protected copiedChart = -1;

	/** The chart spec shown full-size in the lightbox, or null when closed. */
	protected lightboxSpec: CsChartSpec | null = null;

	/**
	 * KPI-card spec → render model. Cards are HTML, not SVG: typography reflows with the
	 * bubble, themes via CSS vars, copies as text and exports to PDF without rasterizing.
	 * One dataset = a flat tile row; several = grouped rows (each dataset label is the
	 * group header, like Power BI's multi-row card split by category).
	 */
	protected cardGroups(spec: CsChartSpec): { header: string; cards: { label: string; value: string; fullValue: string }[] }[] {
		const labels   = spec.data.labels ?? [];
		const datasets = spec.data.datasets ?? [];
		return datasets.map(ds => ({
			header: (datasets.length > 1 && ds.label) ? ds.label : '',
			cards:  (ds.data ?? []).map((v, i) => ({
				label:     labels[i] ?? '',
				value:     formatKpi(v),
				fullValue: v.toLocaleString('en-US')
			}))
		}));
	}

	private readonly el  = inject(ElementRef<HTMLElement>);
	private readonly cdr = inject(ChangeDetectorRef);

	/** Copies a chart to the clipboard as a PNG (computed styles baked in). */
	protected async copyChart(event: Event, chartIndex: number): Promise<void> {
		const figure = (event.target as HTMLElement).closest('figure');
		const svg    = figure?.querySelector('svg');
		if (!svg) { return; }
		try {
			const dataUrl = await svgToPngDataUrl(svg as SVGSVGElement, this.surfaceColor());
			await navigator.clipboard.write([new ClipboardItem({ 'image/png': dataUrlToBlob(dataUrl) })]);
			this.copiedChart = chartIndex;
			this.cdr.markForCheck();
			setTimeout(() => { this.copiedChart = -1; this.cdr.markForCheck(); }, 1500);
		} catch {
			// Clipboard may be unavailable (permissions/insecure context) — fail quietly.
		}
	}

	/** Open the chart full-size in a lightbox over the chat workspace. */
	protected openLightbox(spec: CsChartSpec): void {
		this.lightboxSpec = spec;
	}

	/** Close the lightbox (backdrop click, close button, or Escape). */
	protected closeLightbox(): void {
		if (!this.lightboxSpec) { return; }
		this.lightboxSpec = null;
		this.cdr.markForCheck();
	}

	@HostListener('document:keydown.escape')
	protected onLightboxEscape(): void {
		this.closeLightbox();
	}

	/** Full-size chart height: most of the viewport, leaving room for the panel chrome. */
	protected lightboxChartHeight(): number {
		const h = typeof window !== 'undefined' ? window.innerHeight : 800;
		return Math.max(360, Math.round(h * 0.7));
	}

	/**
	 * Exports this response as a PDF via a print window: the bubble's HTML with every chart
	 * SVG replaced by a rasterized PNG (so the PDF honours the charts as images), carrying the
	 * app's stylesheets so markdown/tables/chips keep their look.
	 */
	protected async downloadPdf(): Promise<void> {
		const bubble = this.el.nativeElement.querySelector('.cs-chat__bubble') as HTMLElement | null;
		if (!bubble) { return; }

		// Strip the app chrome: the export is a document, not a screenshot of the chat —
		// no model badges, tool chips, stats footer, or hover controls.
		const clone = bubble.cloneNode(true) as HTMLElement;
		clone.querySelectorAll(
			'.cs-chat__chart-actions, .cs-chat__footer, .cs-chat__activity, ' +
			'.cs-chat__models, .cs-chat__tool-chip, .cs-chat__artifact-chip'
		).forEach(n => n.remove());

		// Page breaks on section boundaries: each major heading after the first starts a new
		// page. (Done here rather than in CSS — the content is split across several
		// cs-markdown segments, so no CSS selector can identify the document-wide first.)
		clone.querySelectorAll<HTMLElement>('h1.cs-markdown__h, h2.cs-markdown__h, h3.cs-markdown__h')
			.forEach((h, i) => { if (i > 0) { h.style.breakBefore = 'page'; } });

		// Replace each live chart SVG with its rasterized image (index-matched into the clone).
		// Charts are exported on a PURE WHITE background — the document page is white, and the
		// app's warm-paper surface would show as a tinted rectangle around every plot.
		const liveSvgs  = Array.from(bubble.querySelectorAll<SVGSVGElement>('cs-chart svg'));
		const cloneSvgs = Array.from(clone.querySelectorAll('cs-chart svg'));
		for (let i = 0; i < liveSvgs.length; i++) {
			try {
				const img = document.createElement('img');
				img.src = await svgToPngDataUrl(liveSvgs[i], '#ffffff');
				img.style.width = '100%';
				cloneSvgs[i]?.replaceWith(img);
			} catch { /* keep the svg — better than losing the chart */ }
		}

		// Carry the app's <style> tags (emulated-encapsulation attributes survive cloneNode,
		// so scoped selectors keep matching) AND its stylesheet <link>s — the webfonts
		// (IBM Plex / Fraunces) load via Google Fonts links, not style tags.
		const styles = Array.from(document.querySelectorAll('style')).map(s => s.textContent ?? '').join('\n');
		const links  = Array.from(document.querySelectorAll('link[rel="stylesheet"], link[rel="preconnect"]'))
			.map(l => l.outerHTML).join('');
		const win = window.open('', '_blank', 'width=900,height=1100');
		if (!win) { return; }
		win.document.write(
			'<!doctype html><html><head><meta charset="utf-8"><title>Phineas — chat export</title>'
			+ links
			+ `<style>${styles}</style>`
			// Document, not app: flat white page, app typography, charts framed only by
			// their captions. The bubble wrapper is unwrapped — only its content ships.
			+ '<style>'
			+ 'body{background:#fff;color:#212d30;margin:0;padding:2.2rem 2.6rem;max-width:50rem;'
			+ 'font-family:var(--cs-font-sans, "Inter", "Segoe UI", system-ui, sans-serif);}'
			+ '.cs-chat__chart{border:0;padding:0;margin:1rem 0;}'
			+ 'figure,table,pre,.cs-markdown__table-wrap{break-inside:avoid;}'
			+ '.cs-markdown__h{break-after:avoid-page;}'   // a heading never strands at a page bottom
			+ 'p{orphans:3;widows:3;}'
			+ '</style>'
			+ `</head><body>${clone.innerHTML}</body></html>`);
		win.document.close();
		win.focus();
		// Give the embedded images a beat to decode before the print dialog snapshots them.
		setTimeout(() => win.print(), 400);
	}

	private surfaceColor(): string {
		return getComputedStyle(document.documentElement).getPropertyValue('--cs-surface').trim() || '#ffffff';
	}
}

/** Power BI-style compact KPI number: 3.53M / 247.4K / 1,234 (exact value lives in the title). */
function formatKpi(value: number): string {
	const abs = Math.abs(value);
	if (abs >= 1_000_000) { return trimZeros((value / 1_000_000).toFixed(2)) + 'M'; }
	if (abs >= 10_000)    { return trimZeros((value / 1_000).toFixed(1)) + 'K'; }
	return (Math.round(value * 100) / 100).toLocaleString('en-US');
}

function trimZeros(text: string): string {
	return text.replace(/\.?0+$/, '');
}
